import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '@environments/environment';
import { Appointment } from '@models/appointments/appointment';
import { PaymentStatus } from '@models/appointments/payment-status';
import { ChargeableAppointment } from '@models/billing/chargeable-appointment';
import { ConfirmCancelData } from '@models/billing/confirm-cancel-data';
import { FinanceTransactionType } from '@models/finance/finance-transaction-type';
import { InvoicePayment } from '@models/finance/invoice-payment';
import { Invoice } from '@models/invoice/invoice';
import { InvoiceLineItemTax } from '@models/invoice/invoice-line-item-tax';
import { CancellationType } from '@models/payments/cancellation-type.enum';
import { Service } from '@models/service/service';
import { FinanceService } from './finance.service';
import { InvoicesService } from './invoices.service';
import { TreatmentPlanService } from './treatment-planning/treatment-plan.service';
import { UsersService } from './users.service';
import { GenericDialogComponent } from '@app/management/dialogs/generic-confirm/generic-confirm.component';

@Injectable({ providedIn: 'root' })
export class AppointmentCancellationService {
  constructor(
    private http: HttpClient,
    private dialog: MatDialog,
    private invoicesService: InvoicesService,
    private financeService: FinanceService,
    private usersService: UsersService,
    private treatmentPlanService: TreatmentPlanService
  ) {}

  isAppointmentCancellationChargeable(appointmentId: number) {
    return this.http.get<ChargeableAppointment>(
      `${environment.baseUrl}api/Appointments/IsAppointmentCancellationChargeable/${appointmentId}`
    );
  }

  chargeAppointmentCancellation(cancellationType: CancellationType, visitId?: number, appointmentId?: number) {
    if (cancellationType === CancellationType.Appointment) {
      return this.http.post<Invoice>(
        `${environment.baseUrl}api/Appointments/ChargeAppointmentCancellation/${visitId}/${appointmentId}`,
        {}
      );
    }
    return this.http.post<Invoice>(`${environment.baseUrl}api/Visits/ChargeVisitCancellation/${visitId}`, {});
  }

  appointmentCancellationNotCharged(
    cancellationType: CancellationType,
    visitId: number,
    appointmentId: number,
    reason: string
  ) {
    let headers = new HttpHeaders();
    headers = headers.append('Content-Type', 'application/json');

    if (cancellationType === CancellationType.Appointment) {
      return this.http.post<void>(
        `${environment.baseUrl}api/Appointments/AppointmentCancellationNotCharged/${appointmentId}`,
        JSON.stringify(reason),
        { headers }
      );
    }
    return this.http.post<void>(
      `${environment.baseUrl}api/Visits/VisitCancellationNotCharged/${visitId}`,
      JSON.stringify(reason),
      { headers }
    );
  }

  async handleCancellation(
    appointment: Appointment,
    updateAppointment: (appointment: Appointment) => Promise<unknown>
  ): Promise<boolean> {
    const service = appointment.service;
    const isPaid = appointment.paymentStatus === PaymentStatus.Paid;
    const canCancelNonTxPlanPrepayment = isPaid && !service.isLocked;
    let continueCancel = true;
    let chargeTotal = new Service(service).getChargeAmount();

    if (isPaid) {
      const visitInvoice = await this.invoicesService.getInvoiceForService(service.serviceId).toPromise();
      if (visitInvoice) {
        const serviceLineItem = visitInvoice.invoiceLineItems.find((ili) => ili.serviceId === service.serviceId);
        if (serviceLineItem && serviceLineItem.total >= 0) {
          // Check for any membership discounts applied to the line item
          var membershipDiscountLineItem = visitInvoice.invoiceLineItems.find(
            (ili) => ili.relatedInvoiceLineItemId === serviceLineItem.id && ili.patientMembershipId > 0
          );
          if (membershipDiscountLineItem) {
            chargeTotal = serviceLineItem.total + membershipDiscountLineItem.total;
          } else {
            chargeTotal = serviceLineItem.total;
          }
        }
      }
    }

    if ((chargeTotal && chargeTotal > 0 && !service.isPrepaid && isPaid) || service.isLocked) {
      const formattedTotal = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(chargeTotal);
      const dialogRef = this.dialog.open(GenericDialogComponent, {
        width: '330px',
        data: {
          title: canCancelNonTxPlanPrepayment ? 'Refund Payment To Patient Credit' : 'Cannot Cancel Appointment',
          content: canCancelNonTxPlanPrepayment
            ? 'This service has already been paid in a patient invoice. Refund ' +
              formattedTotal +
              ' + tax to patient credit?'
            : 'This service has been locked from the patient chart',
          confirmButtonText: 'OK',
          showCancel: canCancelNonTxPlanPrepayment,
        },
      });

      continueCancel = await dialogRef
        .afterClosed()
        .map((result) => result === 'confirm' && canCancelNonTxPlanPrepayment)
        .toPromise();

      if (!continueCancel) {
        return false;
      }
    }

    const { ConfirmCancelWithReasonDialogComponent } = await import(
      '@app/management/dialogs/confirm-cancel-with-reason/confirm-cancel-with-reason.component'
    );

    const dialogRef = this.dialog.open(ConfirmCancelWithReasonDialogComponent, {
      width: '400px',
      data: {
        result: '',
        selectedCancelReason: '',
        customCancelReason: '',
        appointmentId: appointment.appointmentId,
      },
    });

    const result = await dialogRef.afterClosed().toPromise();
    if (result?.event !== 'confirm') {
      return false;
    }

    const confirmResultData: ConfirmCancelData = result.data;
    const cancelResult = await this.handleCancellationCharge(
      confirmResultData,
      CancellationType.Appointment,
      appointment.visitId,
      appointment.appointmentId
    );

    if (!cancelResult) {
      return false;
    }

    if (confirmResultData.isCancelChargeable && !confirmResultData.chargeCancelAppointment) {
      appointment.reasonCancellationNotCharged = confirmResultData.reasonCancellationNotCharged;
    }

    appointment.cancelledByUserId = this.usersService.loggedInUser.id;
    appointment.cancellationDate = new Date();
    appointment.cancelled = true;
    appointment.cancellationReason = confirmResultData.selectedCancelReason ?? '';
    appointment.cancellationMessage = confirmResultData.customCancelReason ?? '';
    appointment.source = null;
    appointment.className = '';

    if (isPaid && !service.isPrepaid) {
      const taxes: Array<{ serviceId: number; amount: number; iliTaxes: InvoiceLineItemTax[] }> =
        await this.invoicesService
          .calculateLineItemTax([{ serviceId: service.serviceId, productId: null, amount: chargeTotal }])
          .toPromise();
      chargeTotal = taxes && taxes.length > 0 ? taxes[0].amount : chargeTotal;
      const invoiceReturn = await this.invoicesService.refundPaidService(service.serviceId).toPromise();
      const invoicePayment: InvoicePayment = this.invoicesService.getReturnPayment(
        appointment.patientId,
        invoiceReturn.id,
        FinanceTransactionType.Refund,
        chargeTotal,
        'Refunding cancelled appointment to credit'
      );
      await this.financeService.addTransactions(invoicePayment).toPromise();
    }

    await updateAppointment(appointment);
    if (appointment.plannedTreatmentId) {
      this.treatmentPlanService.plannedTreatmentCancelled$.next();
    }
    return true;
  }

  async handleCancellationCharge(
    confirmCancelData: ConfirmCancelData,
    cancellationType: CancellationType,
    visitId?: number,
    appointmentId?: number
  ) {
    let response = true;
    if (confirmCancelData.isCancelChargeable) {
      if (confirmCancelData.chargeCancelAppointment) {
        await this.chargeAppointmentCancellation(cancellationType, visitId, appointmentId)
          .toPromise()
          .catch(async (errorResponse) => {
            console.error(errorResponse);
            let errorMessage;
            if (errorResponse.error?.errors) {
              errorMessage = errorResponse.error.errors[0].fieldErrors[0];
            } else if (errorResponse.message) {
              errorMessage = errorResponse.message;
            } else {
              errorMessage = errorResponse.error;
            }

            const errorContent = '<span class="text-danger">' + errorMessage + '</span>';
            const dialogRef = this.dialog.open(GenericDialogComponent, {
              width: '300px',
              data: {
                title: 'Credit Card Payment Error',
                content:
                  'The cancellation invoice was created but there was a problem processing the credit card payment. ' +
                  errorContent +
                  '<br /><strong>Do you want to cancel the appointment anyway?</strong>',
                confirmButtonText: 'Yes',
                showCancel: true,
                cancelButtonText: 'No',
                panelClass: 'confirm-appointment-dialog-box',
              },
            });
            const errorDialogResponse = await dialogRef.afterClosed().toPromise();
            response = errorDialogResponse === 'confirm';
          });
      } else {
        await this.appointmentCancellationNotCharged(
          cancellationType,
          visitId,
          appointmentId,
          confirmCancelData.reasonCancellationNotCharged
        )
          .toPromise()
          .catch(async (errorResponse) => {
            console.error(errorResponse);
            let errorMessage;
            if (errorResponse.error?.errors) {
              errorMessage = errorResponse.error.errors[0].fieldErrors[0];
            } else if (errorResponse.message) {
              errorMessage = errorResponse.message;
            } else {
              errorMessage = errorResponse.error;
            }

            const errorContent = '<span class="text-danger">' + errorMessage + '</span>';

            const dialogRef = this.dialog.open(GenericDialogComponent, {
              width: '300px',
              data: {
                title: 'Credit Card Payment Error',
                content:
                  'There was a problem processing the credit card payment. <strong>Do you want to continue cancelling the appointment?</strong>\n' +
                  errorContent,
                confirmButtonText: 'Yes',
                showCancel: true,
                cancelButtonText: 'No',
                panelClass: 'confirm-appointment-dialog-box',
              },
            });
            const errorDialogResponse = await dialogRef.afterClosed().toPromise();
            response = errorDialogResponse === 'confirm';
          });
      }
    }
    return response;
  }
}
